import { type AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { useCallback, useMemo } from 'react';
import { trackAuthEvent } from '../observability/authTelemetry';
import { getTeamsAuthTokenSilent } from '../teams';
import {
  authErrorCode,
  clearRedirectAttempt,
  pendingRedirect,
  redirectAlreadyAttempted,
  shouldFallBackToInteractive,
  startGuardedRedirect,
} from './interactiveFallback';
import { apiTokenRequest } from './msalConfig';
import { useTeamsSso } from './TeamsSsoContext';

export function useAuth() {
  const { instance, accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] ?? undefined);
  // Single shared probe — see TeamsSsoContext. Previously duplicated here and
  // in AuthGuard; each mount fired its own Teams getAuthToken call.
  const { inTeams: inTeamsRaw, ready: teamsSsoReady, user: teamsUser } = useTeamsSso();
  const inTeams = inTeamsRaw === true;

  // Synthesise an AccountInfo-shaped object from the Teams context so
  // downstream consumers (Dashboard, useBookmarks) that gate on `account`
  // being truthy keep working when we've skipped MSAL entirely.
  //
  // CRITICAL: wrap in useMemo. An object literal in the render body is a new
  // reference every render, which makes `account` churn and causes any
  // consumer with `account` in a useEffect dep array (e.g. useBookmarks) to
  // refetch on every render => infinite loop + rate-limit 429s.
  const teamsSynthAccount = useMemo<AccountInfo | null>(() => {
    if (!(inTeams && teamsSsoReady === true && teamsUser)) return null;
    return {
      homeAccountId: `${teamsUser.oid}.${teamsUser.tenantId}`,
      environment: 'login.microsoftonline.com',
      tenantId: teamsUser.tenantId,
      username: teamsUser.userPrincipalName,
      localAccountId: teamsUser.oid,
      // `||`, not `??`: empty-string displayName from Teams must fall
      // through to undefined so downstream logic uses username instead.
      name: teamsUser.displayName || undefined,
    };
  }, [inTeams, teamsSsoReady, teamsUser]);

  const account = msalAccount ?? teamsSynthAccount;

  const acquireApiToken = useCallback(async (): Promise<string> => {
    // Teams silent-SSO happy path: Teams SDK caches tokens internally, so a
    // repeat call re-uses its cache or silently refreshes from Entra.
    if (inTeams && teamsSsoReady === true) {
      return getTeamsAuthTokenSilent();
    }

    if (!msalAccount) throw new Error('No signed-in account');
    try {
      const res = await instance.acquireTokenSilent({ ...apiTokenRequest, account: msalAccount });
      clearRedirectAttempt();
      return res.accessToken;
    } catch (err) {
      // Popup inside Teams iframe: only for InteractionRequiredAuthError, as
      // before. acquireApiToken cannot tell a user action from a background
      // refresh, and a popup without a user gesture is blocked, so the
      // timed_out-style codes are not escalated to a popup in Teams.
      if (inTeams) {
        if (!(err instanceof InteractionRequiredAuthError)) throw err;
        trackAuthEvent('auth.token_refresh_interactive', { inTeams, errorCode: authErrorCode(err) });
        const res = await instance.acquireTokenPopup({
          ...apiTokenRequest,
          account: msalAccount,
        });
        clearRedirectAttempt();
        return res.accessToken;
      }

      // Browser: interaction-required errors and hidden-iframe renewal
      // failures (e.g. timed_out after the Entra session expired) both get a
      // redirect; see interactiveFallback.ts.
      if (!shouldFallBackToInteractive(err)) throw err;

      // Another call in this page load already started the redirect: wait
      // for the navigation instead of starting a second one.
      const pending = pendingRedirect();
      if (pending) {
        await pending.catch(() => undefined);
        throw err;
      }
      // A redirect already ran and silent renewal still fails: surface the
      // error rather than bouncing through Entra in a loop.
      if (redirectAlreadyAttempted()) throw err;

      const redirect = startGuardedRedirect(() =>
        instance.acquireTokenRedirect({ ...apiTokenRequest, account: msalAccount }),
      );
      if (!redirect) throw err; // loop guard unavailable (no sessionStorage)
      trackAuthEvent('auth.token_refresh_interactive', { inTeams, errorCode: authErrorCode(err) });
      await redirect;
      throw err;
    }
  }, [instance, msalAccount, inTeams, teamsSsoReady]);

  const signOut = useCallback(() => {
    // Teams silent-SSO has no MSAL session to clear, but sign-out still makes
    // sense: drop any MSAL cache + reload to force a fresh probe. The user's
    // Teams session itself is owned by the Teams host, not us.
    if (inTeams && teamsSsoReady === true && !msalAccount) {
      void instance.clearCache();
      window.location.reload();
      return;
    }
    if (inTeams) {
      void instance.logoutPopup();
    } else {
      void instance.logoutRedirect();
    }
  }, [instance, inTeams, teamsSsoReady, msalAccount]);

  return {
    inTeams,
    teamsSsoReady,
    account,
    displayName: account?.name || account?.username || null,
    username: account?.username ?? null,
    acquireApiToken,
    signOut,
  };
}
